import { effect, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { DbService } from "src/app/shared/services/db.service";
import { TourService } from "src/app/shared/services/tour.service";
import { AlertStore } from "./alert.store";
import { AuthStore } from "./auth.store";
import { DashboardStore } from "./dashboard.store";
import { FiltersStore } from "./filters.store";
import { GatewayStore } from "./gateway.store";
import { MeterStore } from "./meter.store";
import { PlacesStore } from "./places.store";
import { ShowerheadStore } from "./showerhead.store";
import { UserStore } from "./user.store";

const LOG_TAG = "[LIFE-CYCLE-STORE] ";

@Injectable({
  providedIn: "root",
})
export class LifeCycleStore {
  private authStore: AuthStore = inject(AuthStore);
  private userStore: UserStore = inject(UserStore);
  private meterStore: MeterStore = inject(MeterStore);
  private showerheadStore: ShowerheadStore = inject(ShowerheadStore);
  private placeStore: PlacesStore = inject(PlacesStore);
  private gatewayStore: GatewayStore = inject(GatewayStore);
  private filtersStore: FiltersStore = inject(FiltersStore);
  private dashboardStore: DashboardStore = inject(DashboardStore);
  private alertStore: AlertStore = inject(AlertStore);
  private dbService: DbService = inject(DbService);
  private tourService: TourService = inject(TourService);
  private router: Router = inject(Router);

  private readonly loginOngoing = signal<boolean>(false);
  private readonly loginDone = signal<boolean>(false);
  private readonly logoutOngoing = signal<boolean>(false);
  private readonly logoutDone = signal<boolean>(false);

  constructor() {
    effect(
      () => {
        const logged = this.authStore.logged$();
        const logoutDone = this.logoutDone();
        const loginDone = this.loginDone();

        console.log(
          LOG_TAG + "effect / logged changed",
          logged,
          "loginDone=",
          loginDone,
          "logoutDone=",
          logoutDone
        );
        if (logged === null) {
          this._reload();
        } else if (logged === false && !logoutDone) {
          this._logout();
        } else if (logged && !loginDone) {
          this._login();
        }
      },
      { allowSignalWrites: true }
    );
  }

  private _reload() {
    console.log(LOG_TAG + "reload page ?");

    const needAuth = this.authStore.needAuth;
    if (!needAuth) {
      this.userStore.fetchUser();
    }
  }

  private _login() {
    if (this.loginOngoing()) return;
    this.loginOngoing.set(true);

    console.log(LOG_TAG + "login", this.router.url);

    this.userStore.fetchUser();
    this.placeStore.fetchPlaces();
    this.meterStore.fetchMeters();
    this.showerheadStore.fetchShowerheads();
    this.gatewayStore.fetchGateways();
    this.alertStore.fetchAlertDefinitions();
    this.alertStore.fetchAlerts();

    if (this.router.url.includes("login")) {
      this.router.navigate(["/dashboard"]);
    }

    this.loginOngoing.set(false);
    this.loginDone.set(true);
    this.logoutDone.set(false);
  }

  private async _logout() {
    if (this.logoutOngoing()) return;
    this.logoutOngoing.set(true);

    console.log(LOG_TAG + "logout");

    this._clearStores();
    await this.dbService.clearAllCache();

    console.log(LOG_TAG + "redirect to login on logged off");
    this.router.navigate(["/login"]);
    this.logoutOngoing.set(false);
    this.logoutDone.set(true);
    this.loginDone.set(false);
  }

  private _clearStores() {
    console.log(LOG_TAG + "clearStores");

    this.dashboardStore.clear();
    this.gatewayStore.clear();
    this.showerheadStore.clear();
    this.meterStore.clear();
    this.placeStore.clear();
    this.alertStore.clear();
    this.filtersStore.clear();
    this.userStore.clear();
  }
}
